import type { ActivitySettingsModel } from '@/shared/generated'
import type { JSONSchemaType } from 'ajv'

import React, { Fragment } from 'react'
import { prop, propOr } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { RHFDate, RHFSelect } from '@/shared/components/HookForm'
import { Checkbox } from '@/shared/components/ui/checkbox-native'
import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/shared/components/ui/form'
import {
	InputBase,
	InputBaseAdornment,
	InputBaseControl,
	InputBaseInput,
} from '@/shared/components/ui/input-base'
import { Separator } from '@/shared/components/ui/separator'
import {
	ToggleGroup,
	ToggleGroupItem,
} from '@/shared/components/ui/toggle-group'
import { useAppStore } from '@/shared/contexts/app'
import {
	getEnumOptions,
	translateOptions,
} from '@/shared/tools/json-schema-utils'
import { asNumber } from '@/shared/tools/utils'

const weekdays = [
	'monday',
	'tuesday',
	'wednesday',
	'thursday',
	'friday',
	'saturday',
	'sunday',
]
export const ActivityFields = ({
	name,
	schema,
}: {
	name: string
	schema?: JSONSchemaType<ActivitySettingsModel>
}) => {
	const { t } = useTranslation()
	const dateFormat = useAppStore((state) => state.dateFormat)

	return (
		<Fragment>
			<FormField
				name={`${name}.force_inactive`}
				render={({ field }) => (
					<FormItem className='flex flex-row items-start'>
						<div className='flex w-full items-start gap-3 rounded-lg border bg-accent/20 p-3 hover:bg-accent/50 has-[[aria-checked=true]]:border-primary has-[[aria-checked=true]]:bg-primary-50 dark:has-[[aria-checked=true]]:border-primary dark:has-[[aria-checked=true]]:bg-primary-950'>
							<FormControl>
								<Checkbox checked={field.value} onChange={field.onChange} />
							</FormControl>
							<div className='grid gap-1 font-normal'>
								<FormLabel>
									{propOr(
										'Force inactive',
										'properties.force_inactive.title',
										schema
									)}
								</FormLabel>
								<FormDescription>
									{propOr(
										'Force inactive products to be inactive on the start date and active on the end date',
										'properties.force_inactive.description',
										schema
									)}
								</FormDescription>
							</div>
						</div>
					</FormItem>
				)}
			/>
			<div className='flex items-start gap-4'>
				<RHFDate
					format={dateFormat}
					name={`${name}.start_date`}
					label={propOr('Start date', 'properties.start_date.title', schema)}
					description={propOr('', 'properties.start_date.description', schema)}
				/>
				<RHFDate
					format={dateFormat}
					name={`${name}.end_date`}
					label={propOr('End date', 'properties.end_date.title', schema)}
					description={propOr('', 'properties.end_date.description', schema)}
				/>
			</div>
			<Separator />
			<div className='flex items-start gap-4'>
				<FormField
					name={`${name}.repeat_week_interval`}
					render={({ field: { value, onChange } }) => (
						<FormItem className='shrink-0'>
							<FormLabel>
								{propOr(
									'Interval',
									'properties.repeat_week_interval.title',
									schema
								)}
							</FormLabel>
							<InputBase>
								<InputBaseAdornment>
									{t('general_repeat_every', {
										defaultValue: 'Repeat every',
									})}
								</InputBaseAdornment>
								<FormControl>
									<InputBaseControl>
										<InputBaseInput
											defaultValue={value}
											value={value}
											className='max-w-6'
											onChange={(e) => onChange(asNumber(e.target.value))}
											placeholder='0'
										/>
									</InputBaseControl>
								</FormControl>
								<InputBaseAdornment>
									{t('general_weeks', {
										defaultValue: 'weeks',
									})}
								</InputBaseAdornment>
							</InputBase>
							<FormDescription>
								{propOr(
									'',
									'properties.repeat_week_interval.description',
									schema
								)}
							</FormDescription>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					name={`${name}.repeat_days`}
					render={({ field: { value, onChange } }) => (
						<FormItem className='w-full'>
							<FormLabel>
								{propOr(
									'Repeat on weekdays',
									'properties.repeat_days.title',
									schema
								)}
							</FormLabel>
							<ToggleGroup
								value={value?.map(String)}
								type='multiple'
								variant='outline'
								onValueChange={(v) => onChange(v)}
								className='w-full'
							>
								{weekdays.map((day) => (
									<ToggleGroupItem
										className='data-[state=off]:font-normal data-[state=off]:text-muted-foreground'
										value={day}
										key={day}
									>
										{t(day, {
											defaultValue: day,
										})}
									</ToggleGroupItem>
								))}
							</ToggleGroup>
							<FormDescription>
								{propOr('', 'properties.repeat_days.description', schema)}
							</FormDescription>
							<FormMessage />
						</FormItem>
					)}
				/>
			</div>
			<Separator />
			<RHFSelect
				name={`${name}.inactive_strategy`}
				label={propOr(
					'Inactive strategy',
					'properties.inactive_strategy.title',
					schema
				)}
				description={propOr(
					'This strategy will be applied to the products that are currently assigned to the pricing campaign during the inactive period.',
					'properties.inactive_strategy.description',
					schema
				)}
				options={translateOptions(
					getEnumOptions(prop('properties.inactive_strategy', schema)),
					t
				)}
			/>
		</Fragment>
	)
}
